
import React, { useRef, useEffect, useState } from 'react';
import { SectionTitle } from './SectionTitle';
import { useTranslation } from '../i18n/LanguageContext';

interface Pillar {
  number: string;
  title: string;
  description: string;
}

export const FourPillarsSection: React.FC = () => {
    const { t } = useTranslation();
    const pillarsData = t('fourPillars.pillars') as Pillar[];

    const [activeIndex, setActiveIndex] = useState(0);
    const sectionRef = useRef<HTMLDivElement>(null);
    const pillarRefs = useRef<(HTMLDivElement | null)[]>([]);
    const svgPathRef = useRef<SVGPathElement>(null);
    
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = pillarRefs.current.indexOf(entry.target as HTMLDivElement);
                        if (index !== -1) {
                            setActiveIndex(index);
                        }
                    }
                });
            },
            {
                // Ajustado para capturar melhor a intenção do usuário no scroll lento
                rootMargin: "-35% 0px -45% 0px", 
                threshold: 0.1,
            }
        );

        const currentRefs = pillarRefs.current;
        currentRefs.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            currentRefs.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, [pillarsData.length]);

    useEffect(() => {
        const path = svgPathRef.current;
        const sectionEl = sectionRef.current;
        if (!path || !sectionEl) return;
        
        const updatePath = () => {
            const pathLength = path.getTotalLength();
            path.style.strokeDasharray = `${pathLength} ${pathLength}`;
            
            const sectionRect = sectionEl.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            
            // Lógica de cálculo da linha mais suave e centralizada
            const startOffset = viewportHeight * 0.3;
            const scrollRange = sectionRect.height - viewportHeight * 0.4;
            const currentScroll = -sectionRect.top + startOffset;
            
            const progress = Math.max(0, Math.min(1, currentScroll / scrollRange));
            path.style.strokeDashoffset = `${pathLength * (1 - progress)}`;
        };

        window.addEventListener('scroll', updatePath, { passive: true });
        window.addEventListener('resize', updatePath);
        setTimeout(updatePath, 200);

        return () => {
            window.removeEventListener('scroll', updatePath);
            window.removeEventListener('resize', updatePath);
        };
    }, []);

    return (
        <section id="pilares" ref={sectionRef} className="py-20 md:py-32 relative block overflow-visible">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="md:grid md:grid-cols-2 md:gap-20 lg:gap-32 items-start relative">
                    
                    {/* Coluna Esquerda: FIXED / STICKY */}
                    <div className="hidden md:block sticky top-32 h-fit z-30 pb-20">
                        <SectionTitle
                            title={t('fourPillars.title')}
                            subtitle={t('fourPillars.subtitle')}
                            className="text-left mb-12"
                            titleClassName="text-4xl lg:text-5xl font-serif font-bold text-brand-light mb-6 leading-tight"
                            subtitleClassName="text-lg text-brand-text max-w-sm mx-0 leading-relaxed"
                        />
                        
                        <div className="relative flex items-center min-h-[320px]">
                            <div className="absolute left-4 top-0 h-full w-1">
                                <svg width="4" height="100%" viewBox="0 0 4 320" preserveAspectRatio="none" className="h-full overflow-visible">
                                    <path d="M 2 0 V 320" stroke="#1B273F" strokeWidth="4" />
                                    <path
                                        ref={svgPathRef}
                                        d="M 2 0 V 320"
                                        stroke="url(#line-pillares-grad-fluid)"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                        style={{ transition: 'stroke-dashoffset 0.15s ease-out' }}
                                    />
                                    <defs>
                                        <linearGradient id="line-pillares-grad-fluid" x1="0" y1="0" x2="0" y2="100%">
                                            <stop offset="0%" stopColor="#22d3ee" />
                                            <stop offset="100%" stopColor="#0ea5e9" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                            
                            <ul className="relative w-full space-y-10 pl-14">
                                {pillarsData.map((pillar, index) => (
                                    <li key={pillar.number} className="relative flex items-center">
                                        <div className={`absolute -left-14 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all duration-700 cubic-bezier(0.16, 1, 0.3, 1)
                                            ${activeIndex === index ? 'bg-brand-accent border-brand-accent shadow-[0_0_25px_rgba(34,211,238,0.4)] scale-125' : 'bg-brand-primary border-slate-700 scale-100'}
                                        `}>
                                            <div className={`w-2.5 h-2.5 rounded-full transition-all duration-500 ${activeIndex === index ? 'bg-white' : 'bg-slate-700'}`}></div>
                                        </div>
                                        <h3 className={`text-xl lg:text-2xl font-serif font-semibold transition-all duration-700 cubic-bezier(0.16, 1, 0.3, 1) transform ${activeIndex === index ? 'text-brand-light translate-x-3' : 'text-slate-600'}`}>
                                            {pillar.title}
                                        </h3>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    
                    {/* Coluna Direita: ROLÁVEL */}
                    <div className="relative space-y-16 md:space-y-0">
                        <div className="md:hidden mb-16">
                             <SectionTitle
                                title={t('fourPillars.title')}
                                subtitle={t('fourPillars.subtitle')}
                                className="text-center"
                            />
                        </div>
                        
                        {pillarsData.map((pillar, index) => (
                           <div 
                               key={pillar.number} 
                               ref={el => { pillarRefs.current[index] = el; }}
                               className="flex items-center min-h-[60vh] md:min-h-[75vh] py-12 md:py-24"
                            >
                               <div className={`bg-brand-primary/50 p-8 sm:p-10 rounded-2xl shadow-2xl border transition-all duration-1000 cubic-bezier(0.16, 1, 0.3, 1) transform w-full
                                   ${activeIndex === index ? 'card-glow-active scale-[1.03] bg-brand-primary/80 border-brand-accent/30' : 'border-slate-800/30 opacity-10 scale-95 blur-[2px]'}
                               `}>
                                   <div className="flex items-center mb-8">
                                       <span className={`text-5xl font-serif font-bold transition-colors duration-700 ${activeIndex === index ? 'text-brand-accent' : 'text-slate-800'}`}>
                                           {pillar.number}
                                       </span>
                                       <div className="ml-8 h-px flex-grow bg-slate-800/30"></div>
                                   </div>
                                   <h4 className="text-2xl sm:text-3xl font-serif font-semibold text-brand-light mb-6 leading-tight">{pillar.title}</h4>
                                   <p className="text-brand-text text-lg leading-relaxed">{pillar.description}</p>
                               </div>
                           </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
